import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export const ROLES = {
  AGENT: 'agent',
  LENDER: 'lender',
  BORROWER: 'borrower',
};

export const INSTITUTION_TYPES = {
  FACILITY_AGENT: 'facility_agent',
  BANK: 'bank',
  CORPORATE: 'corporate',
  FUND: 'fund',
};

export const ACCOUNT_TYPES = {
  ENTERPRISE: 'enterprise',
  INDIVIDUAL: 'individual',
};

export const ROLE_DESCRIPTIONS = {
  [ROLES.AGENT]: {
    label: 'Facility Agent',
    labelEn: 'Facility Agent',
    description: 'Vue complète de tous les prêts, gestion des covenants et administration de la plateforme',
    descriptionEn: 'Full view of all loans, covenant management and platform administration',
    color: 'purple',
    icon: 'Shield',
  },
  [ROLES.LENDER]: {
    label: 'Prêteur',
    labelEn: 'Lender',
    description: 'Monitoring des prêts et covenants sur vos expositions',
    descriptionEn: 'Monitoring of loans and covenants on your exposures',
    color: 'blue',
    icon: 'Building2',
  },
  [ROLES.BORROWER]: {
    label: 'Emprunteur',
    labelEn: 'Borrower',
    description: 'Gestion de vos prêts, mise à jour des données financières et reporting',
    descriptionEn: 'Manage your loans, update financial data and reporting',
    color: 'green',
    icon: 'Briefcase',
  },
};

export const PERMISSION_CATEGORIES = {
  dashboard: {
    label: 'Tableau de bord',
    labelEn: 'Dashboard',
    permissions: {
      view_dashboard: 'Voir le tableau de bord',
      view_dashboard_stats: 'Voir les statistiques globales',
      view_dashboard_charts: 'Voir les graphiques',
      view_all_loans: 'Voir tous les prêts',
      view_own_loans: 'Voir ses propres prêts',
    },
  },
  loans: {
    label: 'Prêts',
    labelEn: 'Loans',
    permissions: {
      view_loans: 'Voir les prêts',
      view_loan_details: 'Voir les détails d\'un prêt',
      create_loan: 'Créer un prêt',
      edit_loan: 'Modifier un prêt',
      delete_loan: 'Supprimer un prêt',
      archive_loan: 'Archiver un prêt',
    },
  },
  covenants: {
    label: 'Covenants',
    labelEn: 'Covenants',
    permissions: {
      view_covenants: 'Voir les covenants',
      create_covenant: 'Créer un covenant',
      edit_covenant: 'Modifier un covenant',
      delete_covenant: 'Supprimer un covenant',
      update_covenant_value: 'Mettre à jour les valeurs',
    },
  },
  alerts: {
    label: 'Alertes',
    labelEn: 'Alerts',
    permissions: {
      view_alerts: 'Voir les alertes',
      view_own_alerts: 'Voir ses propres alertes',
      manage_alerts: 'Gérer les alertes (marquer lu, résoudre)',
      configure_alerts: 'Configurer les seuils d\'alerte',
      delete_alerts: 'Supprimer les alertes',
    },
  },
  reports: {
    label: 'Rapports',
    labelEn: 'Reports',
    permissions: {
      view_reports: 'Voir les rapports',
      create_report: 'Créer des rapports',
      export_reports: 'Exporter les rapports (PDF, Excel)',
      schedule_reports: 'Planifier des rapports automatiques',
    },
  },
  documents: {
    label: 'Documents',
    labelEn: 'Documents',
    permissions: {
      view_documents: 'Voir les documents',
      upload_documents: 'Téléverser des documents',
      delete_documents: 'Supprimer des documents',
    },
  },
  import: {
    label: 'Import de données',
    labelEn: 'Data Import',
    permissions: {
      import_data: 'Importer des données',
      import_bulk: 'Import en masse',
      import_validate: 'Valider les imports',
    },
  },
  settings: {
    label: 'Paramètres',
    labelEn: 'Settings',
    permissions: {
      view_settings: 'Voir les paramètres',
      manage_settings: 'Modifier les paramètres',
      manage_notifications: 'Configurer les notifications',
      manage_platform: 'Administrer la plateforme',
    },
  },
};

export const PERMISSIONS = {
  [ROLES.AGENT]: [
    'view_dashboard', 'view_dashboard_stats', 'view_dashboard_charts', 'view_all_loans',
    'view_loans', 'view_loan_details', 'create_loan', 'edit_loan', 'delete_loan', 'archive_loan',
    'view_covenants', 'create_covenant', 'edit_covenant', 'delete_covenant', 'update_covenant_value',
    'view_alerts', 'manage_alerts', 'configure_alerts', 'delete_alerts',
    'view_reports', 'create_report', 'export_reports', 'schedule_reports',
    'view_documents', 'upload_documents', 'delete_documents',
    'import_data', 'import_bulk', 'import_validate',
    'view_settings', 'manage_settings', 'manage_notifications', 'manage_platform',
  ],
  [ROLES.LENDER]: [
    'view_dashboard', 'view_dashboard_stats', 'view_dashboard_charts',
    'view_loans', 'view_loan_details',
    'view_covenants',
    'view_alerts', 'view_own_alerts', 'manage_alerts',
    'view_reports', 'export_reports',
    'view_documents',
    'view_settings',
  ],
  [ROLES.BORROWER]: [
    'view_dashboard', 'view_dashboard_stats', 'view_dashboard_charts', 'view_own_loans',
    'view_loans', 'view_loan_details',
    'view_covenants', 'update_covenant_value',
    'view_alerts', 'view_own_alerts', 'manage_alerts',
    'view_reports', 'export_reports',
    'view_documents', 'upload_documents',
    'import_data',
    'view_settings',
  ],
};

export const getPermissionLabel = (permission) => {
  for (const category of Object.values(PERMISSION_CATEGORIES)) {
    if (category.permissions[permission]) {
      return category.permissions[permission];
    }
  }
  return permission;
};

export const getPermissionsByCategory = (role) => {
  const rolePermissions = PERMISSIONS[role] || [];
  const result = {};

  for (const [categoryKey, category] of Object.entries(PERMISSION_CATEGORIES)) {
    const categoryPermissions = {};
    for (const [permKey, permLabel] of Object.entries(category.permissions)) {
      categoryPermissions[permKey] = {
        label: permLabel,
        granted: rolePermissions.includes(permKey),
      };
    }
    result[categoryKey] = {
      label: category.label,
      permissions: categoryPermissions,
    };
  }

  return result;
};


const DEMO_USERS = [
  {
    id: '00000000-0000-0000-0000-000000000001',
    email: 'agent@lma-demo.com',
    password: 'demo2025',
    firstName: 'Sophie',
    lastName: 'Laurent',
    role: ROLES.AGENT,
    institutionType: INSTITUTION_TYPES.FACILITY_AGENT,
    company: 'LMA Agency Services',
    position: 'Senior Facility Agent',
    avatar: null,
    phone: '+44 20 7006 1000',
    createdAt: '2025-01-15',
  },
  {
    id: '00000000-0000-0000-0000-000000000002',
    email: 'lender@bnp-demo.com',
    password: 'demo2025',
    firstName: 'Jean-Pierre',
    lastName: 'Dubois',
    role: ROLES.LENDER,
    institutionType: INSTITUTION_TYPES.BANK,
    company: 'BNP Paribas',
    position: 'Credit Portfolio Manager',
    avatar: null,
    phone: '+33 1 40 14 45 46',
    createdAt: '2025-02-20',
  },
  {
    id: '00000000-0000-0000-0000-000000000003',
    email: 'lender@hsbc-demo.com',
    password: 'demo2025',
    firstName: 'James',
    lastName: 'Thompson',
    role: ROLES.LENDER,
    institutionType: INSTITUTION_TYPES.BANK,
    company: 'HSBC',
    position: 'Loan Syndication Manager',
    avatar: null,
    phone: '+44 20 7991 8888',
    createdAt: '2025-02-22',
  },
  {
    id: '00000000-0000-0000-0000-000000000004',
    email: 'borrower@acme-demo.com',
    password: 'demo2025',
    firstName: 'Marie',
    lastName: 'Schmidt',
    role: ROLES.BORROWER,
    institutionType: INSTITUTION_TYPES.CORPORATE,
    company: 'ACME Industries GmbH',
    position: 'Group Treasurer',
    avatar: null,
    phone: '+49 69 7575 0',
    createdAt: '2025-03-10',
  },
  {
    id: '00000000-0000-0000-0000-000000000005',
    email: 'borrower@greentech-demo.com',
    password: 'demo2025',
    firstName: 'Elena',
    lastName: 'Martinez',
    role: ROLES.BORROWER,
    institutionType: INSTITUTION_TYPES.CORPORATE,
    company: 'GreenTech Solutions SA',
    position: 'CFO',
    avatar: null,
    phone: '+34 91 123 4567',
    createdAt: '2025-03-15',
  },
];

const AuthContext = createContext(null);


const isElectron = () => {
  return typeof window !== 'undefined' && window.electronAPI !== undefined;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [useSupabaseAuth] = useState(!isElectron());


  useEffect(() => {
    console.log('[Auth] Initializing, useSupabaseAuth:', useSupabaseAuth);

    let isMounted = true;

    const timeout = setTimeout(() => {
      console.warn('[Auth] Timeout - forcing isLoading to false');
      if (isMounted) setIsLoading(false);
    }, 5000);

    if (useSupabaseAuth) {

      console.log('[Auth] Calling getSession...');
      supabase.auth.getSession().then(({ data, error }) => {
        if (!isMounted) return;
        console.log('[Auth] getSession response:', { data, error });
        clearTimeout(timeout);
        if (error) {
          console.error('[Auth] getSession error:', error);
          setIsLoading(false);
          return;
        }
        const session = data?.session;
        console.log('[Auth] getSession result:', session ? 'has session' : 'no session');
        setSession(session);
        if (session?.user) {
          fetchProfile(session.user.id);
        } else {
          setIsLoading(false);
        }
      }).catch(err => {
        if (!isMounted) return;
        console.error('[Auth] getSession catch error:', err);
        clearTimeout(timeout);
        setIsLoading(false);
      });

      const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, newSession) => {
        if (!isMounted) return;
        console.log('[Auth] onAuthStateChange:', event, newSession ? 'has session' : 'no session');

        // Ignore TOKEN_REFRESHED events that might cause flickering
        if (event === 'TOKEN_REFRESHED') {
          console.log('[Auth] Token refreshed, keeping current session');
          return;
        }

        // Only update session if it's actually different
        if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
          setSession(newSession);
          if (newSession?.user) {
            await fetchProfile(newSession.user.id);
          }
        } else if (event === 'SIGNED_OUT') {
          setSession(null);
          setUser(null);
          setIsLoading(false);
        }
      });

      return () => {
        isMounted = false;
        clearTimeout(timeout);
        subscription.unsubscribe();
      };
    } else {

      const storedUser = localStorage.getItem('covenant_user') || sessionStorage.getItem('covenant_user');
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);
        } catch (e) {
          localStorage.removeItem('covenant_user');
          sessionStorage.removeItem('covenant_user');
        }
      }
      setIsLoading(false);
    }
  }, [useSupabaseAuth]);


  const fetchProfile = async (userId) => {
    console.log('[Auth] fetchProfile called for userId:', userId);
    try {
      console.log('[Auth] Making profiles query...');


      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Profile fetch timeout after 10s')), 10000)
      );

      const queryPromise = supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      const { data: profile, error } = await Promise.race([queryPromise, timeoutPromise]);

      console.log('[Auth] fetchProfile response:', { profile, error });

      if (error) throw error;

      setUser({
        id: profile.id,
        email: profile.email,
        firstName: profile.first_name,
        lastName: profile.last_name,
        role: profile.role,
        institutionType: profile.institution_type,
        company: profile.company,
        position: profile.position,
        phone: profile.phone,
        avatar: profile.avatar_url,
        createdAt: profile.created_at,
      });
      console.log('[Auth] User set successfully');
    } catch (err) {
      console.error('[Auth] Error fetching profile:', err);
      setError(err.message);
    } finally {
      console.log('[Auth] fetchProfile finally - setting isLoading to false');
      setIsLoading(false);
    }
  };


  const loginWithSupabase = async (email, password) => {
    setError(null);
    setIsLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;


      await supabase
        .from('profiles')
        .update({ last_login: new Date().toISOString() })
        .eq('id', data.user.id);


      await supabase.from('activity_logs').insert({
        user_id: data.user.id,
        type: 'connection',
        category: 'auth',
        action: 'login',
        message: 'Successful login',
        user_agent: navigator.userAgent,
      });

      return { success: true, user: data.user };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  };


  const loginWithDemo = async (email, password, rememberMe = false) => {
    setError(null);
    setIsLoading(true);

    await new Promise((resolve) => setTimeout(resolve, 800));

    const foundUser = DEMO_USERS.find(
      (u) => u.email === email && u.password === password
    );

    if (!foundUser) {
      setIsLoading(false);
      setError('Invalid email or password');
      return { success: false, error: 'Invalid email or password' };
    }

    const { password: _, ...userWithoutPassword } = foundUser;

    setUser(userWithoutPassword);

    if (rememberMe) {
      localStorage.setItem('covenant_user', JSON.stringify(userWithoutPassword));
    } else {
      sessionStorage.setItem('covenant_user', JSON.stringify(userWithoutPassword));
    }

    setIsLoading(false);
    return { success: true, user: userWithoutPassword };
  };


  const login = async (email, password, rememberMe = false) => {
    if (useSupabaseAuth) {
      return loginWithSupabase(email, password);
    }
    return loginWithDemo(email, password, rememberMe);
  };


  const signup = async (userData) => {
    if (!useSupabaseAuth) {
      return { success: false, error: 'Signup not available in demo mode' };
    }

    setError(null);
    setIsLoading(true);

    try {
      const { data, error } = await supabase.auth.signUp({
        email: userData.email,
        password: userData.password,
        options: {
          data: {
            first_name: userData.firstName,
            last_name: userData.lastName,
            role: userData.role || 'lender',
            company: userData.company,
          },
        },
      });

      if (error) throw error;

      return { success: true, user: data.user };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  };


  const logout = async () => {
    try {
      if (useSupabaseAuth && session) {

        if (user) {
          try {
            await supabase.from('activity_logs').insert({
              user_id: user.id,
              type: 'session',
              category: 'auth',
              action: 'logout',
              message: 'User logged out',
            });
          } catch (logError) {
            console.warn('Failed to log logout activity:', logError);
          }
        }

        await supabase.auth.signOut();
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {

      setUser(null);
      setSession(null);
      setIsLoading(false);
      setError(null);
      localStorage.removeItem('covenant_user');
      sessionStorage.removeItem('covenant_user');
    }
  };


  const updateProfile = async (updates) => {
    setIsLoading(true);

    if (useSupabaseAuth && user) {
      try {
        const { error } = await supabase
          .from('profiles')
          .update({
            first_name: updates.firstName,
            last_name: updates.lastName,
            company: updates.company,
            position: updates.position,
            phone: updates.phone,
            avatar_url: updates.avatar,
          })
          .eq('id', user.id);

        if (error) throw error;

        const updatedUser = { ...user, ...updates };
        setUser(updatedUser);
        setIsLoading(false);
        return { success: true, user: updatedUser };
      } catch (err) {
        setIsLoading(false);
        return { success: false, error: err.message };
      }
    }


    await new Promise((resolve) => setTimeout(resolve, 500));
    const updatedUser = { ...user, ...updates };
    setUser(updatedUser);

    const storedInLocal = localStorage.getItem('covenant_user');
    if (storedInLocal) {
      localStorage.setItem('covenant_user', JSON.stringify(updatedUser));
    } else {
      sessionStorage.setItem('covenant_user', JSON.stringify(updatedUser));
    }

    setIsLoading(false);
    return { success: true, user: updatedUser };
  };


  const changePassword = async (currentPassword, newPassword) => {
    setIsLoading(true);

    if (useSupabaseAuth) {
      try {
        const { error } = await supabase.auth.updateUser({
          password: newPassword,
        });

        if (error) throw error;

        setIsLoading(false);
        return { success: true };
      } catch (err) {
        setIsLoading(false);
        return { success: false, error: err.message };
      }
    }


    await new Promise((resolve) => setTimeout(resolve, 500));
    const userInDemo = DEMO_USERS.find((u) => u.id === user.id);
    if (userInDemo && userInDemo.password !== currentPassword) {
      setIsLoading(false);
      return { success: false, error: 'Current password is incorrect' };
    }

    if (userInDemo) {
      userInDemo.password = newPassword;
    }

    setIsLoading(false);
    return { success: true };
  };


  const requestPasswordReset = async (email) => {
    if (useSupabaseAuth) {
      try {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`,
        });

        if (error) throw error;

        return { success: true, message: 'Reset link sent to your email' };
      } catch (err) {
        return { success: false, error: err.message };
      }
    }

    await new Promise((resolve) => setTimeout(resolve, 800));
    return { success: true, message: 'If this email exists, a reset link has been sent.' };
  };


  const hasPermission = (permission) => {
    if (!user) return false;
    const userPermissions = PERMISSIONS[user.role] || [];
    return userPermissions.includes(permission);
  };

  const hasRole = (requiredRole) => {
    if (!user) return false;
    return user.role === requiredRole;
  };

  const isAgent = () => user?.role === ROLES.AGENT;
  const isLender = () => user?.role === ROLES.LENDER;
  const isBorrower = () => user?.role === ROLES.BORROWER;

  const getDemoUsers = () => {
    return DEMO_USERS.map(({ password, ...user }) => user);
  };

  const value = {
    user,
    session,
    isLoading,
    error,
    isAuthenticated: !!user,
    useSupabaseAuth,
    login,
    signup,
    logout,
    updateProfile,
    changePassword,
    hasPermission,
    hasRole,
    isAgent,
    isLender,
    isBorrower,
    requestPasswordReset,
    getDemoUsers,
    clearError: () => setError(null),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
